import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { useLocation, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { TOUR_CHAPTERS } from './tour-chapters'
import { TourContext } from './tour-context'

const COMPLETED_KEY = 'iwarranty_tour_completed_chapters'
const HUB_SEEN_KEY = 'iwarranty_tour_hub_seen'

function loadCompleted(): Set<string> {
  try {
    const raw = localStorage.getItem(COMPLETED_KEY)
    if (raw) return new Set(JSON.parse(raw) as string[])
  } catch { /* ignore */ }
  return new Set()
}

function saveCompleted(s: Set<string>) {
  localStorage.setItem(COMPLETED_KEY, JSON.stringify([...s]))
}

export function TourProvider({ children }: { children: ReactNode }) {
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const [activeChapterId, setActiveChapterId] = useState<string | null>(null)
  const [currentStepIndex, setCurrentStepIndex] = useState<number | null>(null)
  const [completedChapters, setCompletedChapters] = useState<Set<string>>(loadCompleted)
  const [hasSeenHub, setHasSeenHub] = useState(
    () => localStorage.getItem(HUB_SEEN_KEY) === 'true',
  )

  const activeChapter = activeChapterId
    ? TOUR_CHAPTERS.find((c) => c.id === activeChapterId) ?? null
    : null
  const currentStep =
    activeChapter && currentStepIndex !== null
      ? activeChapter.steps[currentStepIndex] ?? null
      : null
  const totalSteps = activeChapter?.steps.length ?? 0

  // ── Auto-redirect to /guide on first dashboard visit ──
  useEffect(() => {
    if (hasSeenHub) return
    if (location.pathname === '/dashboard') {
      localStorage.setItem(HUB_SEEN_KEY, 'true')
      // Navigate on next tick to avoid setState-in-effect
      const timer = setTimeout(() => {
        setHasSeenHub(true)
        navigate('/guide', { replace: true })
      }, 0)
      return () => clearTimeout(timer)
    }
  }, [location.pathname, hasSeenHub, navigate])

  const startChapter = useCallback((chapterId: string) => {
    const chapter = TOUR_CHAPTERS.find((c) => c.id === chapterId)
    if (!chapter) return

    if (chapter.group) {
      const updated = new Set(completedChapters)
      for (const c of TOUR_CHAPTERS) {
        if (c.group === chapter.group) updated.delete(c.id)
      }
      setCompletedChapters(updated)
      saveCompleted(updated)
    }

    setActiveChapterId(chapterId)
    setCurrentStepIndex(0)
  }, [completedChapters])

  const nextStep = useCallback(() => {
    if (activeChapter === null || currentStepIndex === null) return
    const nextIndex = currentStepIndex + 1

    if (nextIndex >= activeChapter.steps.length) {
      // Mark current chapter complete
      const updatedCompleted = new Set(completedChapters)
      updatedCompleted.add(activeChapter.id)
      setCompletedChapters(updatedCompleted)
      saveCompleted(updatedCompleted)

      // If this chapter belongs to a group, auto-start the next incomplete one
      if (activeChapter.group) {
        const groupChapters = TOUR_CHAPTERS.filter(
          (c) => c.group === activeChapter.group && !c.comingSoon,
        )
        const nextInGroup = groupChapters.find(
          (c) => c.id !== activeChapter.id && !updatedCompleted.has(c.id),
        )
        if (nextInGroup) {
          navigate(nextInGroup.route)
          setTimeout(() => {
            setActiveChapterId(nextInGroup.id)
            setCurrentStepIndex(0)
          }, 600)
          return
        }
      }

      setActiveChapterId(null)
      setCurrentStepIndex(null)
      return
    }

    const nextStepDef = activeChapter.steps[nextIndex]
    const currentUrl = location.pathname + location.search
    if (nextStepDef?.route && nextStepDef.route !== currentUrl) {
      navigate(nextStepDef.route)
      setTimeout(() => setCurrentStepIndex(nextIndex), 600)
    } else {
      setCurrentStepIndex(nextIndex)
    }
  }, [activeChapter, currentStepIndex, completedChapters, location.pathname, location.search, navigate])

  const prevStep = useCallback(() => {
    if (activeChapter === null || currentStepIndex === null || currentStepIndex <= 0) return
    const prevIndex = currentStepIndex - 1
    const prevStepDef = activeChapter.steps[prevIndex]
    const targetRoute = prevStepDef?.route ?? activeChapter.route
    const currentUrl = location.pathname + location.search
    if (targetRoute && targetRoute !== currentUrl) {
      navigate(targetRoute)
      setTimeout(() => setCurrentStepIndex(prevIndex), 600)
    } else {
      setCurrentStepIndex(prevIndex)
    }
  }, [activeChapter, currentStepIndex, location.pathname, location.search, navigate])

  const skipChapter = useCallback(() => {
    // Dismiss the tour but stay on the current page - the user is mid-task
    // and shouldn't be bounced back to the hub.
    setActiveChapterId(null)
    setCurrentStepIndex(null)
  }, [])

  const resetTour = useCallback(() => {
    setActiveChapterId(null)
    setCurrentStepIndex(null)
    setCompletedChapters(new Set())
    setHasSeenHub(false)
    localStorage.removeItem(COMPLETED_KEY)
    localStorage.removeItem(HUB_SEEN_KEY)
  }, [])

  const nextGroupChapter = useMemo(() => {
    if (!activeChapter?.group) return null
    const groupChapters = TOUR_CHAPTERS.filter(
      (c) => c.group === activeChapter.group && !c.comingSoon,
    )
    const next = groupChapters.find(
      (c) => c.id !== activeChapter.id && !completedChapters.has(c.id),
    )
    return next ? t(`tour.chapter.${next.id}.title`, next.title) : null
  }, [activeChapter, completedChapters, t])

  const value = useMemo(
    () => ({
      activeChapter,
      currentStepIndex,
      currentStep,
      totalSteps,
      completedChapters,
      hasSeenHub,
      nextGroupChapter,
      startChapter,
      nextStep,
      prevStep,
      skipChapter,
      resetTour,
    }),
    [
      activeChapter,
      currentStepIndex,
      currentStep,
      totalSteps,
      completedChapters,
      hasSeenHub,
      nextGroupChapter,
      startChapter,
      nextStep,
      prevStep,
      skipChapter,
      resetTour,
    ],
  )

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>
}
